import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  CANCELED_EVENT_REGISTRATION_EMAIL_PREVIEW_QUERY,
  type CanceledEventRegistrationEmailPreviewQueryResponse,
  type CanceledEventRegistrationEmailPreviewQueryVariables,
  type EmailPreview
} from "../_queries/emailPreviewQueries";

export function useCanceledEventRegistrationEmailPreview(registrationId: string) {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    CanceledEventRegistrationEmailPreviewQueryResponse,
    CanceledEventRegistrationEmailPreviewQueryVariables
  >(CANCELED_EVENT_REGISTRATION_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(async () => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    setLoading(true);
    setPreview(null);
    setError(null);

    const result = await fetchPreview({
      variables: { registrationId }
    });

    if (requestId !== activeRequestIdRef.current) {
      return result;
    }

    if (result.error) {
      setError(result.error);
      setPreview(null);
    } else {
      setPreview(result.data?.canceledEventRegistrationEmailPreview ?? null);
      setError(null);
    }

    setLoading(false);
    return result;
  }, [fetchPreview, registrationId]);

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
